<template>
    <div
        class="relative bg-white overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg pt-5 col-span-12 md:col-span-6 lg:col-span-4 flex flex-col justify-between"
        :class="{
            'excluded-card': nextCard.hidden,
            'recommended-card': !nextCard.hidden,
        }"
    >
        <CardExclusion
            :card="nextCard"
            :offenders="offenders"
            v-if="nextCard.hidden"
        />

        <a
            v-if="userSettings.is_admin"
            :href="'/admin/cards/' + nextCard.id + '/edit'"
            target="blank"
            class="block absolute top-2 right-2 hover:text-gray-400"
        >
            <i class="fa-regular fa-pen-to-square"></i>
        </a>

        <div class="card-upper-container flex-1">
            <div class="px-5 mb-5 flex flex-col flex-1 h-full">
                <div
                    class="card-image relative md:px-5 md:mb-3 flex justify-between items-center"
                >
                    <a
                        :href="nextCard.application_link"
                        target="_blank"
                        class="w-1/3 xl:w-full"
                    >
                        <img
                            :src="`${cdnUrl}/${nextCard.card_image?.path}/_small/${nextCard.card_image?.file_name}`"
                            class="object-contain max-h-24 md:max-h-36 w-auto mx-auto mb-3"
                        />
                    </a>
                    <SpecialTag
                        v-if="nextCard.special_offer"
                        :text="'Special Offer'"
                    />
                    <h3
                        class="text-sm md:text-lg text-gray-800 font-bold mb-4 relative pl-8 !leading-none md:hidden ml-4 w-2/3"
                    >
                        <span
                            class="text-white text-sm mr-2 font-bold bg-orange rounded-full w-5 h-5 lg:w-6 lg:h-6 text-center inline-flex flex-col leading-none items-center justify-center absolute left-0 top-0"
                        >
                            <span class="relative">
                                {{ nextCardNumber(nextCard) }}
                            </span>
                        </span>
                        <a
                            :href="nextCard.application_link"
                            target="_blank"
                            class="hover:text-gray-400 text-base font-semibold !leading-tight"
                            v-if="nextCard && nextCard.application_link"
                        >
                            {{ nextCard.title }}
                        </a>
                        <span
                            class="text-base font-semibold !leading-tight"
                            v-else
                        >
                            {{ nextCard.title }}
                        </span>
                    </h3>
                </div>

                <h3
                    class="text-sm md:text-lg text-gray-800 font-bold mb-4 relative pl-8 !leading-none hidden md:block"
                >
                    <span
                        class="text-white text-sm mr-2 font-bold bg-orange rounded-full w-6 h-6 text-center inline-flex flex-col leading-none items-center justify-center absolute left-0 top-0"
                    >
                        <span class="relative">
                            {{ nextCardNumber(nextCard) }}
                        </span>
                    </span>
                    <a
                        :href="nextCard.application_link"
                        target="_blank"
                        class="hover:text-gray-400 text-base font-semibold !leading-tight"
                        v-if="nextCard && nextCard.application_link"
                    >
                        {{ nextCard.title }}
                    </a>
                    <span class="text-base font-semibold !leading-tight" v-else>
                        {{ nextCard.title }}
                    </span>
                </h3>
                <div v-if="nextCard.rich_text">
                    <p class="text-xs text-gray-400 mb-1">Current Offer</p>
                </div>
                <div v-html="nextCard.rich_text"></div>
                <div
                    class="mb-4"
                    v-if="nextCard.affiliate_link_type === 'non_affiliate_link'"
                >
                    <p class="text-xs text-gray-400 italic leading-tight">
                        {{
                            nextCard.issuing_bank.non_affiliate_link_disclosure
                        }}
                    </p>
                </div>

                <CouldContributeToFiveTwentyFour
                    :could_contribute_to_five_twenty_four="
                        nextCard.could_contribute_to_five_twenty_four
                    "
                />

                <div
                    class="card-specs flex flex-row justify-between mt-auto mb-4 items-center"
                >
                    <p class="text-xs text-gray-700">
                        Annual Fee:
                        <span class="text-base font-bold text-gray-800">
                            ${{ nextCard.annual_fee }}
                        </span>
                        <span
                            v-if="
                                nextCard.issuing_bank.name ===
                                    'American Express' ||
                                nextCard.issuing_bank.name === 'Citi'
                            "
                            class="block"
                        >
                            <a
                                :href="nextCard.rates_and_fees_link"
                                target="_blank"
                                v-if="
                                    (nextCard.issuing_bank.name ===
                                        'American Express' ||
                                        nextCard.issuing_bank.name ===
                                            'Citi') &&
                                    nextCard.rates_and_fees_link
                                "
                                class="text-orange hover:text-orange-400"
                            >
                                Rates & Fees
                            </a>
                        </span>
                    </p>
                    <p v-if="nextCard.program">
                        <span
                            class="bg-gray-100 text-gray-600 px-3 py-0.5 text-xs font-medium rounded-full ml-1"
                        >
                            {{ goodProgramName(nextCard.program.type) }} Card
                        </span>
                    </p>
                </div>
            </div>
        </div>

        <div class="points-tips-thoughts mt-auto">
            <div class="points-tips bg-gray-100 border-b border-[#CFD9DE]">
                <div class="py-1 pr-3">
                    <div class="flex justify-between items-center py-2">
                        <h4 class="font-bold w-5/6">
                            <a
                                href="#"
                                class="py-1 px-5 text-sm w-full block"
                                @click.prevent="
                                    nextCard.showTipsList =
                                        !nextCard.showTipsList
                                "
                            >
                                How to Earn Points
                            </a>
                        </h4>
                        <a
                            class="text-orange text-2xl leading-0 w-1/6 text-right"
                            href="#"
                            @click.prevent="
                                nextCard.showTipsList = !nextCard.showTipsList
                            "
                            role="button"
                        >
                            <i
                                class="fa-light fa-angle-down fa-fw transition-all"
                                :class="{
                                    '-rotate-180': nextCard.showTipsList,
                                }"
                            ></i>
                        </a>
                    </div>
                    <ul
                        class="tips-list pl-5 pb-2"
                        v-if="nextCard.showTipsList"
                    >
                        <li
                            class="flex mb-1"
                            v-for="(tip, tIndex) in JSON.parse(
                                nextCard.points_tips
                            )"
                            :key="'tip-' + tIndex"
                        >
                            <span class="pr-2 font-bold w-2/12">
                                {{ tip.label }}
                            </span>
                            <span class="pl-2 pr-1 text-sm w-10/12">
                                {{ tip.value }}
                            </span>
                        </li>
                    </ul>
                </div>
            </div>

            <div class="thoughts bg-gray-100">
                <div class="py-1 pr-3">
                    <div class="flex justify-between items-center py-2">
                        <h4 class="font-bold w-5/6">
                            <a
                                href="#"
                                class="py-1 px-5 text-sm w-full block"
                                @click.prevent="
                                    nextCard.showThoughts =
                                        !nextCard.showThoughts
                                "
                            >
                                10xT Thoughts
                            </a>
                        </h4>
                        <a
                            class="text-orange text-2xl leading-0 w-1/6 text-right"
                            href="#"
                            @click.prevent="
                                nextCard.showThoughts = !nextCard.showThoughts
                            "
                            role="button"
                        >
                            <i
                                class="fa-light fa-angle-down fa-fw transition-all"
                                :class="{
                                    '-rotate-180': nextCard.showThoughts,
                                }"
                            ></i>
                        </a>
                    </div>
                    <div
                        v-if="nextCard.showThoughts"
                        v-html="nextCard.thoughts"
                        class="text-sm py-3 px-5 cardthoughts"
                    ></div>
                </div>
            </div>
        </div>

        <div class="card-lower-container py-2 xl:py-0 px-5">
            <div
                class="card-link flex flex-col justify-between xl:mt-8"
                v-if="
                    nextCard.application_link &&
                    nextCard.issuing_bank.link_relationship_type === 'direct'
                "
            >
                <a
                    :href="nextCard.application_link"
                    class="btn btn-orange !ml-0 !text-md"
                    v-if="nextCard.issuing_bank.link_button_visible"
                    target="_blank"
                >
                    Apply Now
                    <i class="fa-regular fa-lock ml-2"></i>
                </a>

                <p
                    class="text-center"
                    v-if="nextCard.issuing_bank.link_button_visible"
                >
                    <small>
                        on {{ nextCard.issuing_bank.name }}'s secure site
                    </small>
                </p>
            </div>
            <div
                class="card-link flex flex-col justify-between xl:mt-7 text-center"
                v-if="
                    nextCard.application_link &&
                    nextCard.issuing_bank.link_relationship_type === 'indirect'
                "
            >
                <a
                    :href="nextCard.application_link"
                    class="btn btn-orange !ml-0 xl:mb-7 !text-md"
                    v-if="nextCard.issuing_bank.link_button_visible"
                    target="_blank"
                >
                    Learn How to Apply
                    <i class="fa-light fa-arrow-right ml-2"></i>
                </a>
                <a
                    :href="nextCard.application_link"
                    v-else="nextCard.issuing_bank.link_button_visible"
                    class="text-md font-semibold text-orange hover:text-orange-400 xl:mb-9"
                    target="_blank"
                >
                    Learn How to Apply
                    <i class="fa-light fa-arrow-right ml-2"></i>
                </a>
            </div>
        </div>
    </div>
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { useAppStore } from '@/store/app';
import { useNextCardStore } from '@/store/nextcard';

import CardExclusion from '@/components/next-card/card-exclusion.vue';
import SpecialTag from '@/components/next-card/special-tag.vue';
import CouldContributeToFiveTwentyFour from '@/components/next-card/contribute524Count.vue';

export default {
    name: 'NextCardCard',
    props: {
        nextCard: {
            type: Object,
        },
    },
    components: {
        CardExclusion,
        SpecialTag,
        CouldContributeToFiveTwentyFour,
    },
    computed: {
        ...mapState(useAppStore, ['userSettings']),
        ...mapState(useNextCardStore, ['filteredNextCards', 'offenders']),
        cdnUrl() {
            return window.cdnUrl;
        },
    },
    methods: {
        ...mapActions(useNextCardStore, ['goodProgramName']),
        nextCardNumber(nextCard) {
            return this.filteredNextCards.indexOf(nextCard) + 2;
        },
    },
    created() {},
    mounted() {},
};
</script>
